import React from 'react'
import SeoPage from '../components/SeoPage'

import Img from '../components/Image'
import { graphql, Link } from 'gatsby'

const ContentPage = ({ data, location, ...props }) => {
    return (
        <SeoPage
            leadImage={data.img0.childImageSharp.fluid.src}
            location={location}
            title="Accusation de délit de fuite ou de conduite dangereuse!"
            description="Nous vous présentons les infractions criminelles de conduite dangereuse et de délit de fuite, ainsi que les conséquences qui en résultent!"
            fr={undefined}
            lawType="criminal"
            specialtyText="pour Conduite dangereuse et délit de fuite">
            <p>
                Au Canada, le <em>Code criminel</em> sanctionne plusieurs
                infractions liées à la conduite, notamment le délit de fuite et
                la conduite dangereuse. Être accusé de l’une ou l’autre de ces
                infractions est une affaire sérieuse, car les conséquences
                peuvent être lourdes.
            </p>
            <p>
                <strong>
                    Ces deux infractions criminelles sont traitées sévèrement
                    par les tribunaux canadiens, puisqu’elles compromettent la
                    sécurité publique.{' '}
                </strong>
            </p>
            <p>
                Il est donc essentiel de consulter rapidement un avocat en droit
                criminel si vous êtes accusé de l’une de ces infractions.
            </p>
            <p>
                <Img
                    fluid={data.img0.childImageSharp.fluid}
                    title="Qu’est-ce qu’un délit de fuite?"
                    alt=""
                />
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Dans cet article, nous vous expliquons le délit
                                de fuite et la conduite dangereuse, leurs
                                conséquences ainsi que l’importance d’être
                                représenté par un avocat!
                            </strong>
                        </p>
                    </td>
                </tr>
            </table>
            <h2>QU’EST-CE QU’UN DÉLIT DE FUITE?</h2>
            <p>
                <strong>
                    Le délit de fuite est une infraction criminelle prévue à
                    l’article{' '}
                </strong>
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/page-47.html#docCont">
                    320.16
                </Link>
                <strong>
                    {' '}
                    du <em>Code criminel</em>.{' '}
                </strong>
            </p>
            <ul>
                <li>
                    <strong>
                        Elle survient lorsqu’une personne, au volant d’un moyen
                        de transport, est impliquée dans un accident avec une
                        autre personne ou un autre moyen de transport et choisit
                        de ne pas s’arrêter, de ne pas s’identifier ou de ne pas
                        porter assistance à une personne blessée.
                    </strong>
                </li>
            </ul>
            <p>
                <strong>
                    Selon la gravité des conséquences de l’accident, le{' '}
                    <em>Code criminel</em> distingue trois niveaux de délit de
                    fuite :
                </strong>
            </p>
            <h3>
                Omission de s’arrêter à la suite d’un accident : article
                320.16(1){' '}
            </h3>
            <p>
                Il s’agit du niveau de base du délit de fuite, qui s’applique
                lorsque le conducteur sait qu’il a été impliqué dans un accident
                ou ne s’en soucie pas, omet sans excuse raisonnable de
                s’arrêter, ne fournit pas ses nom et adresse, et ne porte pas
                assistance à toute personne blessée ou semblant avoir besoin
                d’aide.
            </p>
            <p>
                Cette infraction peut être traitée par deux modes de poursuite
                ayant chacune ses peines maximales:
            </p>
            <ul>
                <li>
                    Soit comme un acte criminel, passible d’un emprisonnement
                    maximal de dix ans.
                </li>
                <li>
                    Soit par procédure sommaire, avec un emprisonnement maximal
                    de deux ans moins un jour.
                </li>
            </ul>
            <h3>
                Délit de fuite ayant entraîné des lésions corporelles :article
                320.16(2)
            </h3>
            <p>
                Ce degré du délit de fuite s’applique lorsque l’accident a causé
                des lésions corporelles à une autre personne, et que le
                conducteur en est conscient ou choisit délibérément de ne pas en
                tenir compte.{' '}
            </p>
            <p>
                Contrairement au niveau de base de délit de fuite, où les
                conséquences peuvent se limiter à des dommages matériels, ce
                type d’infraction implique des conséquences physiques réelles.
            </p>
            <ul>
                <li>
                    Le <em>Code criminel</em> définit{' '}
                    <strong>
                        les lésions corporelles comme des blessures qui portent
                        atteinte à la santé ou au bien-être d’une personne et
                        qui ne sont ni passagères, ni sans importance
                    </strong>
                    .{' '}
                </li>
            </ul>
            <p>
                Il peut s’agir, par exemple, de fractures, de blessures
                internes, de traumatismes psychologiques durables ou de toute
                autre atteinte ayant des répercussions significatives sur
                l’intégrité de la victime.{' '}
            </p>
            <p>
                La peine encourue pour ce type de délit de fuite est
                naturellement plus sévère. Voici les sanctions prévues par la
                loi :
            </p>
            <ul>
                <li>
                    Une poursuite par acte criminel peut entraîner une peine
                    maximale de 14 ans d’emprisonnement.
                </li>
                <li>
                    En cas de poursuite par procédure sommaire, la peine
                    maximale est de deux ans moins un jour d’emprisonnement et
                    une amende maximale de 5 000 $.
                </li>
                <li>
                    Toutefois, peu importe le mode de poursuite choisi, la peine
                    minimale pour cette infraction est une amende obligatoire de
                    1 000 $ pour une première infraction, 30 jours de prison
                    pour une deuxième infraction, et 120 jours d’emprisonnement
                    pour une troisième infraction.
                </li>
            </ul>
            <h3>Délit de fuite ayant entraîné la mort : article 320.16(3)</h3>
            <p>
                Le degré le plus grave du délit de fuite est atteint lorsque
                l’accident a entraîné la mort d’une personne, ou lorsqu’une
                personne blessée succombe à ses blessures par la suite.{' '}
            </p>
            <p>
                <Img
                    fluid={data.img1.childImageSharp.fluid}
                    title="Délit de fuite ayant entraîné la mort!"
                    alt=""
                />
            </p>
            <ul>
                <li>
                    Pour être reconnu coupable de cette infraction,{' '}
                    <strong>
                        il faut que le conducteur sache que l’accident a causé
                        la mort d’une autre personne, ou des lésions corporelles
                        ayant entraîné la mort, ou qu’il ne s’en soucie pas!
                    </strong>
                </li>
            </ul>
            <p>La peine encourue pour ce type de délit de fuite est grave :</p>
            <ul>
                <li>
                    Cette infraction constitue un acte criminel passible de
                    l’emprisonnement à perpétuité.
                </li>
                <li>
                    Les peines minimales obligatoires sont les mêmes que pour le
                    délit de fuite ayant causé des lésions corporelles : une
                    amende de 1 000 $ pour une première infraction, 30 jours de
                    prison pour une deuxième infraction, et 120 jours
                    d’emprisonnement pour une troisième infraction.
                </li>
            </ul>
            <h3>
                Comment que le juge détermine-t-il la peine pour un délit de
                fuite?
            </h3>
            <p>
                Lors de la détermination de la peine, le tribunal doit d’abord
                respecter le mode de poursuite choisi, qui établit des barèmes
                de peine maximaux et minimaux.{' '}
            </p>
            <p>
                Ensuite, il prend en compte plusieurs circonstances aggravantes
                prévues par la loi, susceptibles d’entraîner une peine plus
                sévère. Ces éléments comprennent :
            </p>
            <ul>
                <li>
                    L’accident a entraîné des blessures ou la mort de plusieurs
                    personnes.
                </li>
                <li>
                    Le conducteur participait à une course ou une épreuve de
                    vitesse.
                </li>
                <li>
                    Un enfant de moins de 16 ans était passager du véhicule.
                </li>
                <li>
                    Le véhicule était conduit contre rémunération (chauffeur
                    professionnel).
                </li>
                <li>
                    Le conducteur avait une alcoolémie élevée (120 mg d’alcool
                    ou plus par 100 ml de sang).
                </li>
                <li>
                    Le véhicule était un gros véhicule à moteur (camion,
                    autobus, etc.).
                </li>
                <li>
                    Le conducteur n’avait pas le droit de conduire (suspension,
                    permis expiré ou inexistant).
                </li>
            </ul>
            <h4>Interdiction de conduire </h4>
            <p>
                En plus d’une peine d’amende ou d’emprisonnement, le tribunal
                peut imposer une interdiction de conduire tout véhicule pour une
                durée déterminée.
            </p>
            <p> Cette durée varie selon la gravité de l’infraction :</p>
            <ul>
                <li>
                    Si l’infraction est punissable d’un emprisonnement à
                    perpétuité, comme dans le cas d’un délit de fuite ayant
                    causé la mort, la durée de l’interdiction est laissée à la
                    discrétion du tribunal, en plus de la peine de prison.
                </li>
                <li>
                    Pour une infraction passible d’un emprisonnement de plus de
                    5 ans, comme le délit de fuite ayant entraîné des lésions
                    corporelles, le tribunal peut ordonner une interdiction
                    maximale de 10 ans.
                </li>
                <li>
                    Pour les infractions de moindre gravité, comme le délit de
                    fuite simple poursuivit par voie sommaire, l’interdiction
                    maximale est de 3 ans.
                </li>
            </ul>
            <p>
                Cette interdiction s’applique à la conduite dans les lieux
                publics tels que les rues, chemins et routes, prend effet
                immédiatement après son prononcé, et le tribunal doit
                communiquer clairement cette interdiction à l’accusé.
            </p>
            <p>
                <Img
                    fluid={data.img2.childImageSharp.fluid}
                    title="Conséquences d’une condamnation pour délit de fuite!"
                    alt=""
                />
            </p>
            <p>
                Bref, le délit de fuite est une infraction criminelle grave au
                Canada, dont la sévérité dépend des conséquences de l’accident.
                Qu’il s’agisse de dommages, de blessures ou de décès, quitter
                les lieux sans s’arrêter peut mener à des peines de prison et à
                une interdiction de conduire. L’obligation de s’arrêter est
                absolue. Même en cas d’accident mineur, il faut s’identifier et
                aider au besoin!
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            En cas d’accusation de délit de fuite, il est
                            fortement recommandé de{' '}
                            <strong>
                                consulter un avocat spécialisé en droit criminel
                            </strong>
                            , qui pourra analyser les circonstances, vérifier la
                            légalité des procédures, défendre vos droits et
                            éventuellement soulever des moyens de défense!
                        </p>
                    </td>
                </tr>
            </table>
            <h2>QU’EST-CE QUE LA CONDUITE DANGEREUSE?</h2>
            <p>
                La conduite dangereuse est une infraction criminelle au Canada
                qui, tout comme le délit de fuite, touche directement à la
                sécurité routière. Elle se produit lorsqu’un{' '}
                <strong>
                    conducteur manœuvre un véhicule de manière imprudente ou
                    négligente, au point de mettre en danger la vie ou la
                    sécurité du public.{' '}
                </strong>
            </p>
            <p>
                Selon l’article{' '}
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/section-320.13.html">
                    320.13
                </Link>{' '}
                du <em>Code criminel</em>, cette infraction est commise
                lorsqu’un conducteur agit d’une façon dangereuse pour le public,
                en tenant compte des circonstances comme l’état de la route, la
                météo, la circulation ou l’environnement immédiat.
            </p>
            <ul>
                <li>
                    <strong>
                        La conduite dangereuse est évaluée non seulement à
                        partir des gestes du conducteur, mais aussi en fonction
                        du contexte
                    </strong>
                    . Ainsi, une manœuvre qui serait jugée acceptable sur une
                    route vide et sèche peut devenir dangereuse sur une route
                    glacée et bondée.{' '}
                </li>
            </ul>
            <p>
                De la même façon qu’un conducteur impliqué dans un accident a
                l’obligation légale de s’arrêter (sous peine d’être accusé de
                délit de fuite), tout conducteur a l’obligation d’adapter sa
                conduite aux circonstances pour éviter de créer un danger.
            </p>
            <p>
                <strong>
                    La conduite dangereuse est divisée en trois degrés, suivant
                    un modèle similaire à celui du délit de fuite :
                </strong>
            </p>
            <h3>Conduite dangereuse simple : article 320.13(1)</h3>
            <p>
                Le premier niveau est celui de la conduite dangereuse simple,
                qui ne cause ni blessure ni décès. C’est l’équivalent du délit
                de fuite « de base », soit l’omission de s’arrêter après un
                accident sans conséquence physique et entraîne les mêmes peines.
            </p>
            <p>Cette infraction peut être poursuivie :</p>
            <ul>
                <li>
                    Soit comme un acte criminel, passible d’un emprisonnement
                    maximal de dix ans.
                </li>
                <li>
                    Soit par procédure sommaire, avec un emprisonnement maximal
                    de deux ans moins un jour.
                </li>
            </ul>
            <h3>
                Conduite dangereuse ayant causé des lésions corporelles :
                article 320.13(2)
            </h3>
            <p>
                Le deuxième niveau est celui de la conduite dangereuse ayant
                causé des lésions corporelles. Cette infraction, plus grave,
                équivaut au délit de fuite ayant entraîné des lésions
                corporelles et entraîne les mêmes peines :
            </p>
            <ul>
                <li>
                    Une poursuite par acte criminel peut entraîner une peine
                    maximale de 14 ans d’emprisonnement.
                </li>
                <li>
                    En cas de poursuite par procédure sommaire, la peine
                    maximale est de deux ans moins un jour d’emprisonnement et
                    une amende maximale de 5 000 $.
                </li>
            </ul>
            <p>
                <Img
                    fluid={data.img3.childImageSharp.fluid}
                    title="Conduite dangereuse ayant causé des lésions corporelles!"
                    alt=""
                />
            </p>
            <ul>
                <li>
                    Toutefois, peu importe le mode de poursuite choisi, la peine
                    minimale pour cette infraction est une amende obligatoire de
                    1 000 $ pour une première infraction, 30 jours de prison
                    pour une deuxième infraction, et 120 jours d’emprisonnement
                    pour une troisième infraction.
                </li>
            </ul>
            <h3>Conduite dangereuse ayant causé la mort : article 320.13(3)</h3>
            <p>
                Le niveau le plus grave de cette infraction est la conduite
                dangereuse ayant causé la mort. Ce degré équivaut au délit de
                fuite ayant entraîné un décès, et les deux entraînent les mêmes
                peines :
            </p>
            <ul>
                <li>
                    Acte criminel passible de l’emprisonnement à perpétuité.
                </li>
                <li>
                    Les peines minimales obligatoires comprennent une amende de
                    1 000 $ pour une première infraction, 30 jours de prison
                    pour une deuxième, et 120 jours d’emprisonnement pour une
                    troisième infraction.
                </li>
            </ul>
            <h3>
                Comment le juge détermine-t-il la peine pour conduite
                dangereuse?
            </h3>
            <p>
                Lorsqu’il détermine la peine pour conduite dangereuse, le
                tribunal applique les mêmes critères que pour le délit de fuite,
                en respectant d’abord le mode de poursuite choisi, qui fixe des
                peines minimales et maximales. Ensuite, il tient compte de
                diverses circonstances aggravantes prévues par la loi,
                susceptibles d’alourdir la peine, telles que :
            </p>
            <ul>
                <li>
                    L’accident a causé des blessures ou la mort de plusieurs
                    personnes.
                </li>
                <li>
                    Le conducteur participait à une course ou à une épreuve de
                    vitesse.
                </li>
                <li>Un enfant de moins de 16 ans était passager.</li>
                <li>
                    Le véhicule était conduit à des fins rémunérées (chauffeur
                    professionnel).
                </li>
                <li>Le taux d’alcoolémie du conducteur était élevé </li>
                <li>
                    Le véhicule était un poids lourd (camion, autobus, etc.).
                </li>
                <li>
                    Le conducteur n’était pas autorisé à conduire (suspension,
                    permis expiré ou inexistant).
                </li>
            </ul>
            <h4>L’interdiction de conduire </h4>
            <p>
                Par ailleurs, tout comme le délit de fuite, la conduite
                dangereuse peut entraîner une interdiction de conduire à la
                discrétion du tribunal, en plus de l’amende et de la peine de
                prison.
            </p>
            <p>
                La durée de l’interdiction de conduire dépend du type
                d’infraction :
            </p>
            <ul>
                <li>
                    Pour la conduite dangereuse causant la mort (comme pour le
                    délit de fuite mortel), il n’y a pas de limite maximale : le
                    juge peut interdire de conduire pour une durée qu’il estime
                    appropriée.
                </li>
                <li>
                    Pour les infractions passibles de plus de cinq ans, comme la
                    conduite dangereuse ayant causé des lésions corporelles et
                    la conduite dangereuse simple, l’interdiction peut aller
                    jusqu’à dix ans.
                </li>
                <li>
                    Pour les cas moins graves, la durée maximale est de trois
                    ans, comme pour le cas de la conduite dangereuse simple
                    poursuivie par voie sommaire.
                </li>
            </ul>
            <p>
                <Img
                    fluid={data.img4.childImageSharp.fluid}
                    title="Interdiction de conduire après condamnation pour conduite dangereuse!"
                    alt=""
                />
            </p>
            <p>
                Il est important de noter que, pour les deux types d’infractions
                (délit de fuite et conduite dangereuse), une condamnation
                entraîne une inscription au casier judiciaire. Cela peut avoir
                des répercussions sur la{' '}
                <Link to="https://avocatsalcoolauvolant.ca/voyager-etats-unis-alcool-volant/">
                    <strong>capacité à voyager</strong>
                </Link>
                , à obtenir certains emplois ou à accéder au logement.{' '}
            </p>
            <h2>
                <strong>
                    <strong>
                        Que se passe-t-il lorsqu’une personne est accusée à la
                        fois de conduite dangereuse et de délit de fuite?
                    </strong>
                </strong>
            </h2>
            <p>
                Lorsqu’une personne est accusée simultanément de conduite
                dangereuse et de délit de fuite liés au même événement, il est
                naturel de se demander comment les peines sont appliquées.{' '}
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            Prenons l’exemple d’un conducteur qui roule à une
                            vitesse excessive sur une route glissante, perd le
                            contrôle de son véhicule, provoque un accident avec
                            un autre véhicule et quitte les lieux sans
                            s’arrêter. Dans ce cas, il pourrait faire face à des
                            accusations de conduite dangereuse et de délit de
                            fuite pour le même évènement.
                        </p>
                    </td>
                </tr>
            </table>
            <ul>
                <li>
                    Contrairement à ce que l’on pourrait penser,{' '}
                    <strong>les peines ne s’additionnent pas</strong>. Au
                    Canada, lorsqu’une personne est reconnue coupable de
                    plusieurs infractions criminelles découlant d’un même
                    évènement, les peines sont purgées en même temps.{' '}
                </li>
            </ul>
            <p>
                Cela signifie que si un tribunal impose, par exemple, une peine
                de quatre ans pour conduite dangereuse et trois ans pour délit
                de fuite pour un même évènement,{' '}
                <strong>
                    la personne purgera seulement la peine la plus longue,
                </strong>{' '}
                soit quatre ans.
            </p>
            <p>
                Seules les infractions distinctes dans le temps peuvent
                entraîner des peines consécutives. Par exemple, si un conducteur
                cause un accident sans s’arrêter un jour, puis est arrêté le
                lendemain pour conduite dangereuse, les peines pourraient alors
                être additionnées, car il s’agit de deux événements distincts.
            </p>
            <p>
                En résumé, pour des faits liés à un même événement, une seule
                peine s’applique, la plus sévère, évitant ainsi la double
                sanction.
            </p>
            <h2>
                Vous avez été arrêté pour conduite dangereuse et délit de fuite?
                Consultez un avocat spécialisé en droit criminel!
            </h2>
            <p>
                Si vous êtes accusé de conduite dangereuse, de délit de fuite,
                ou des deux, il est essentiel de consulter rapidement un avocat
                spécialisé en droit criminel.
            </p>
            <p>
                Ces deux infractions criminelles sont graves et comportent des
                peines sévères pouvant affecter votre liberté, votre emploi et
                votre vie personnelle. Un avocat expérimenté pourra analyser les
                circonstances précises de votre dossier, vérifier la légalité
                des preuves et élaborer une stratégie de défense adaptée.
            </p>
            <p>
                Dans certains cas,{' '}
                <strong>
                    un avocat est en mesure d’obtenir un acquittement ou, si
                    cela n’est pas possible, il tentera de négocier afin
                    d’obtenir une peine plus clémente
                </strong>
                . Ne sous-estimez jamais l’importance d’une défense spécialisée
                et de la représentation d’un avocat pour protéger au mieux vos
                droits et obtenir un verdict favorable!
            </p>
            <p>
                <Img
                    fluid={data.img5.childImageSharp.fluid}
                    title="Avocat pour délit de fuite et conduite dangereuse!"
                    alt=""
                />
            </p>
            <p>
                <strong>
                    Remplissez notre formulaire rapide et gratuit pour trouver
                    facilement un avocat spécialisé en droit criminel.
                </strong>
            </p>
            <p>
                <strong>
                    Dès votre demande reçue, nous vous mettons en contact
                    rapidement avec un avocat qualifié près de chez vous. Ce
                    service est entièrement gratuit et sans engagement!
                </strong>
            </p>
        </SeoPage>
    )
}

export default ContentPage

export const pageQuery = graphql`
    query ($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
        img0: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/delit-fuite-infraction-criminelle.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img1: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/delit-fuite-cause-mort.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img2: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/consequences-condamnation-delit-fuite.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img3: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/peine-conduite-dangereuse-lesions-corporelles.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img4: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/interdiction-conduire-condamnation-conduite-dangereuse.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img5: file(
            relativePath: {
                eq: "delit-fuite-conduite-dangereuse/avocat-delit-fuite-conduite-dangereuse.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
    }
`
